// Ngb date picker to work it needs configuration in Schema file
// We have 2 ways to perform validations
// (A) NgbDatePickers own error object >> set 'isActive' to false (It will compare date with its own range)
// (B) Custom Validator >> set 'isActive; to true (It will compare date with its related date picker)

// Ngb datepicker provides its own Error Objects, to make it work we have to set its Range ie. enable required dates and disable other dates
// 1. requiredBefore >> If we enter date manually which is less than Minimum date
// 2. requiredAfter >> If we enter date manually which is greater than Maximum date
// 3. invalid >> If we enter date manually which is incorrect in format

// Schema Changes:
// minValue >> boundaryValue: minimum date which should be enabled below this date all will be disabled
// maxValue >> boundaryValue: maximum date which should be enabled above this date all will be disabled

import { Component, Input, OnChanges, Output, EventEmitter } from '@angular/core';
import { NgbDateParserFormatter, NgbDateStruct } from '@ng-bootstrap/ng-bootstrap';
import moment from 'moment';
import { ERRORS, Constants } from '../../models/constants';
import { FormGroup } from '@angular/forms';
import { SubSink } from 'subsink';
import { throwError } from 'rxjs';
import { CoreCommonService } from '../../providers/services/common.service';
import { CoreHttpService } from '../../utilities/services/http.service';

@Component({
  selector: 'app-ngb-datepicker',
  templateUrl: './ngb-datepicker.component.html'
})
export class NgbDatePickerComponent extends NgbDateParserFormatter implements OnChanges {
  @Input() fieldControl;
  @Input() form;
  @Output() controlReady = new EventEmitter<boolean>();
  @Input() dynamicControlScope: any;
  @Input() parentScope: any;
  @Input() isBundle?: boolean;
  minDate = {};
  maxDate = {};
  private eventSubscriptionsList: SubSink;

  constructor(private coreCommonService: CoreCommonService, private coreHttpService: CoreHttpService) {
    super();
  }

  /**
   * A callback method that is invoked if any data-bound properties change
   */
  ngOnChanges() {
    this.eventSubscriptionsList = new SubSink();
    this.handleControlLoad();
    if (
      this.fieldControl?.validations?.minValue?.hasOwnProperty('boundaryValue') &&
      this.fieldControl.validations.minValue['boundaryValue'] !== '' &&
      this.fieldControl.validations.minValue['boundaryValue'] !== undefined
    ) {
      this.minDate = this.parse(moment().add(this.fieldControl.validations.minValue['boundaryValue'], 'days').format('DD/MM/YYYY'));
    } else {
      this.minDate = { year: 1900, month: 1, day: 1 };
    }
    if (
      this.fieldControl?.validations?.maxValue?.hasOwnProperty('boundaryValue') &&
      this.fieldControl.validations.maxValue['boundaryValue'] !== '' &&
      this.fieldControl.validations.maxValue['boundaryValue'] !== undefined
    ) {
      this.maxDate = this.parse(moment().add(this.fieldControl.validations.maxValue['boundaryValue'], 'days').format('DD/MM/YYYY'));
    } else {
      this.maxDate = { year: moment().year() + 50, month: 1, day: 1 };
    }
  }

  /**
   * This method will handle control load
   * NOTE: Sequence of function calls need to be exactly same as below
   */
  private async handleControlLoad() {
    await this.registerEvents();
    this.notifyControlLoadComplete();
  }

  /**
   * This method will register the events
   */
  private async registerEvents() {
    try {
      const valueChange = this.registerDateValueChange(this.fieldControl, this.form);
      const eventEmitter = this.registerChangeEvent(this.fieldControl, this.form);
      await Promise.all([valueChange, eventEmitter]);
      return true;
    } catch (error) {
      console.log(error);
    }
  }

  /**
   * This method will register change emitter event
   */
  private registerDateValueChange(field: object, form: FormGroup) {
    try {
      const changeEmitterFields = field['changeEmitterFields'] || [];
      for (const changeEmitterField of changeEmitterFields) {
        // INFO: Do not add distinctUntilChanged filter since it will block populate chained dependent dropdown's
        this.eventSubscriptionsList.sink = form.get(changeEmitterField['targetFieldLogicalName']).valueChanges.subscribe((value) => {
          this.callFunctionByName(changeEmitterField.functionName, field, value, form, changeEmitterField.params);
        });
      }
      return true;
    } catch (error) {
      throwError(error);
    }
  }

  /**
   * This method will register dropdown value changes
   */
  private registerChangeEvent(field: object, form: FormGroup) {
    try {
      if (!field.hasOwnProperty('onChangeEvent') || field['onChangeEvent'].length === 0) {
        return;
      }

      for (const eventSchema of field['onChangeEvent']) {
        this.eventSubscriptionsList.sink = form.get(field['logicalName']).valueChanges.subscribe((value) => {
          // value = this.format(value);
          this.callFunctionByName(eventSchema.functionName, field, value, form, eventSchema.params);
        });
      }
      return true;
    } catch (error) {
      throw error;
    }
  }

  /**
   * This method will call the schema functions by name
   */
  async callFunctionByName(fnName: string, ...params: any) {
    try {
      let scope: any;
      if (this[fnName]) {
        scope = this;
      } else if (this.dynamicControlScope[fnName]) {
        scope = this.dynamicControlScope;
      } else if (this.parentScope[fnName]) {
        scope = this.parentScope;
      } else {
        throw new Error(`${ERRORS.FUNCTION_NOT_FOUND}: ${fnName}`);
      }
      params ? scope[fnName](...params) : scope[fnName]();
    } catch (error) {
      throwError(`${ERRORS.FUNCTION_CALL_FAILED}: ${error}`);
    }
    return true;
  }

  /**
   * This method will listen for any update in date field
   */
  private async updateListenerDateField(targetField: object, selectedValue: any, form: FormGroup, params?: any[]) {
    try {
      let data: any;
      if (targetField['defaultValueOptionSet'] && targetField['defaultValueOptionSet'].length !== 0) {
        data = targetField['defaultValueOptionSet'];
      } else {
        const api = this.coreCommonService.extractApiAsPerDomain(targetField['optionsListApi']);
        const constructedParam = this.coreCommonService.extractAndConstructParams(api?.params, form);

        data = await this.coreHttpService
          .doCheck(api['url'], api?.type, constructedParam)
          .toPromise()
          .then((val) => val)
          .catch((err) => throwError(err));
      }
      if (data?.ExpectedDate) {
        this.form.get(this.fieldControl?.logicalName).setValue(data?.ExpectedDate);
      }
    } catch (error) {
      console.log(ERRORS.ERROR_OCCURED, ' : ', error);
    }
    return true;
  }

  /**
   * This method will notify when control is ready
   */
  private notifyControlLoadComplete() {
    this.controlReady.emit(true);
  }

  /**
   * This method will close other opened date pickers
   * @param datePickerReference - date picker reference
   * @param logicalName - logical name
   */
  closeOtherDatePicker(datePickerReference, logicalName) {
    datePickerReference.toggle();
  }

  /**
   * This method will be called on select of any value from datepicker
   * @param event - event
   * @param logicalName - logical name
   */
  onSelect(event, logicalName) {
    // TODO:  while setting value to the dependent FormControl check if its value is less than current form control and then only
    // update its value
    // INFO: below code is commented bcoz it is setting the already selected value to form field
    // if (this.fieldControl.hasOwnProperty('changeEmitterFields') && this.fieldControl['changeEmitterFields'].length > 0) {
    //   for (const targetField of this.fieldControl['changeEmitterFields']) {
    //     if (this.form.get(targetField.targetFieldLogicalName).value !== null) {
    //       this.form.get(targetField.targetFieldLogicalName).setValue(this.form.get(targetField.targetFieldLogicalName).value);
    //     }
    //   }
    // }
  }

  /**
   * This method will add '/' after input of date in datepicker
   */
  addSlash(event) {
    const numChars = event.target.value.length;
    // Don't consider backspace
    if (event.keyCode !== 8) {
      if (numChars === 2 || numChars === 5) {
        event.target.value += '/';
      }
    }
  }

  /**
   * This method will Parse the given string to an NgbDateStruct
   * @param value - value
   */
  parse(value: string): NgbDateStruct {
    try {
      if (value) {
        const dateParts = value.trim().split('/');
        if (dateParts.length === 1 && this.isNumber(dateParts[0])) {
          return { year: this.toInteger(dateParts[0]), month: null, day: null };
        } else if (dateParts.length === 2 && this.isNumber(dateParts[0]) && this.isNumber(dateParts[1])) {
          return { year: this.toInteger(dateParts[0]), month: this.toInteger(dateParts[1]), day: null };
        } else if (dateParts.length === 3 && this.isNumber(dateParts[0]) && this.isNumber(dateParts[1]) && this.isNumber(dateParts[2])) {
          return { day: this.toInteger(dateParts[0]), month: this.toInteger(dateParts[1]), year: this.toInteger(dateParts[2]) };
        }
      }
      return null;
    } catch (error) {
      console.log(error);
    }
  }

  /**
   * Formats the given NgbDateStruct to a string
   * @param date - date
   */
  format(date: NgbDateStruct): string {
    // return date ?
    //     `${date.year}/${this.isNumber(date.month) ?
    //         this.padNumber(date.month) : ''}/${this.isNumber(date.day) ? this.padNumber(date.day) : ''}` :
    //     '';
    return date
      ? `${this.isNumber(date.day) ? this.padNumber(date.day) : ''}/${this.isNumber(date.month) ? this.padNumber(date.month) : ''}/${
          date.year
        }`
      : '';
  }

  /**
   * check if value is number or not
   */
  isNumber(value: any): value is number {
    return !isNaN(this.toInteger(value));
  }

  /**
   * converts value to integer
   */
  toInteger(value: any): number {
    return parseInt(`${value}`, 10);
  }

  /**
   * change the value to required structure
   */
  padNumber(value: number) {
    if (this.isNumber(value)) {
      return `0${value}`.slice(-2);
    } else {
      return '';
    }
  }

  /**
   * getter function to check mandatory fields
   */
  get isMandatory(): boolean {
    try {
      return this.fieldControl.validations.mandatory.value;
    } catch (error) {
      return false;
    }
  }
}
